/** All GLSL lives here. One uber-shader for lit geometry, plus sky, shadow, post. */

export const UBER_VS = `#version 300 es
precision highp float;
layout(location=0) in vec3 aPos;
layout(location=1) in vec3 aNrm;
layout(location=2) in vec3 aCol;
layout(location=3) in vec3 aEmi;
layout(location=4) in vec4 aI0;
layout(location=5) in vec4 aI1;
layout(location=6) in vec4 aI2;
layout(location=7) in vec4 aI3;
layout(location=8) in vec4 aICol;

uniform mat4 uProj;
uniform mat4 uView;
uniform mat4 uModel;
uniform mat4 uShadowVP;
uniform bool uInstanced;
uniform float uTime;
uniform float uWind;

out vec3 vWorldPos;
flat out vec3 vNormal;
out vec3 vColor;
out vec3 vEmissive;
out vec4 vShadowCoord;

void main() {
  mat4 model = uInstanced ? mat4(aI0, aI1, aI2, aI3) : uModel;
  vec4 wp = model * vec4(aPos, 1.0);
  vec3 n = normalize(transpose(inverse(mat3(model))) * aNrm);
  if (uWind > 0.0) {
    float sway = sin(uTime * 1.7 + wp.x * 0.35 + wp.z * 0.27) * 0.6 + sin(uTime * 2.9 + wp.z * 0.8 + wp.x * 0.1) * 0.4;
    float h = max(aPos.y, 0.0);
    wp.x += sway * uWind * h * 0.12;
    wp.z += cos(uTime * 1.3 + wp.x * 0.2) * uWind * h * 0.06;
  }
  vWorldPos = wp.xyz;
  vNormal = n;
  vColor = uInstanced ? aCol * aICol.rgb : aCol;
  vEmissive = uInstanced ? aEmi + vColor * aICol.a : aEmi;
  vShadowCoord = uShadowVP * wp;
  gl_Position = uProj * uView * wp;
}
`;

export const UBER_FS = `#version 300 es
precision highp float;
precision highp sampler2DShadow;
precision highp samplerCubeShadow;

in vec3 vWorldPos;
flat in vec3 vNormal;
in vec3 vColor;
in vec3 vEmissive;
in vec4 vShadowCoord;

uniform vec3 uSunDir;
uniform vec3 uSunColor;
uniform vec3 uSkyColor;
uniform vec3 uGroundColor;
uniform vec3 uFogColor;
uniform vec2 uFogRange;
uniform vec3 uCamPos;
uniform sampler2DShadow uShadowMap;
uniform float uShadowOn;
uniform float uShadowTexel;
uniform vec3 uPointPos;
uniform vec3 uPointColor;
uniform float uPointRadius;
uniform samplerCubeShadow uPointShadow;
uniform float uPointShadowOn;
uniform float uPointFar;
uniform float uEmissiveMul;
uniform float uFlash;
uniform float uAlpha;
uniform float uAmbientMul;

layout(location=0) out vec4 oColor;
layout(location=1) out vec4 oNormal;

float sunShadow(vec3 n) {
  vec3 sc = vShadowCoord.xyz / vShadowCoord.w * 0.5 + 0.5;
  if (sc.x < 0.0 || sc.x > 1.0 || sc.y < 0.0 || sc.y > 1.0 || sc.z > 1.0) return 1.0;
  float ndl = max(dot(n, uSunDir), 0.0);
  float bias = 0.0008 + 0.0025 * (1.0 - ndl);
  float s = 0.0;
  for (int y = -1; y <= 1; y++) {
    for (int x = -1; x <= 1; x++) {
      s += texture(uShadowMap, vec3(sc.xy + vec2(float(x), float(y)) * uShadowTexel, sc.z - bias));
    }
  }
  return s / 9.0;
}

void main() {
  vec3 n = normalize(vNormal);
  float ndl = dot(n, uSunDir);
  // three-step toon ramp with softened edges
  float ramp = smoothstep(-0.08, 0.04, ndl) * 0.5 + smoothstep(0.3, 0.42, ndl) * 0.5;
  float sh = uShadowOn > 0.5 ? sunShadow(n) : 1.0;
  ramp *= sh;
  vec3 hemi = mix(uGroundColor, uSkyColor, n.y * 0.5 + 0.5) * uAmbientMul;
  vec3 lit = vColor * (uSunColor * ramp + hemi);

  if (uPointRadius > 0.0) {
    vec3 L = uPointPos - vWorldPos;
    float d = length(L);
    L /= max(d, 1e-4);
    float att = clamp(1.0 - d / uPointRadius, 0.0, 1.0);
    att *= att;
    float pndl = dot(n, L);
    float pramp = smoothstep(-0.05, 0.1, pndl) * 0.55 + smoothstep(0.4, 0.6, pndl) * 0.45;
    float psh = 1.0;
    if (uPointShadowOn > 0.5) {
      float bias = 0.02 + 0.05 * (1.0 - max(pndl, 0.0));
      psh = texture(uPointShadow, vec4(-L, (d - bias) / uPointFar));
    }
    lit += vColor * uPointColor * att * pramp * psh;
  }

  lit += vEmissive * uEmissiveMul;
  lit = mix(lit, vec3(1.0), uFlash);

  float dist = distance(uCamPos, vWorldPos);
  float fog = clamp((dist - uFogRange.x) / max(uFogRange.y - uFogRange.x, 0.001), 0.0, 1.0);
  fog *= 1.0 - clamp(length(vEmissive) * uEmissiveMul * 0.4, 0.0, 0.5);
  vec3 col = mix(lit, uFogColor, fog);
  oColor = vec4(col, uAlpha);
  oNormal = vec4(n * 0.5 + 0.5, 1.0);
}
`;

/** Depth-only fragment for the sun shadow pass (uses UBER_VS). */
export const SHADOW_FS = `#version 300 es
precision highp float;
void main() {}
`;

/** Point-light shadow: writes linear distance / far so samplerCubeShadow can compare. */
export const POINT_SHADOW_FS = `#version 300 es
precision highp float;
in vec3 vWorldPos;
uniform vec3 uPointPos;
uniform float uPointFar;
void main() {
  gl_FragDepth = clamp(distance(vWorldPos, uPointPos) / uPointFar, 0.0, 1.0);
}
`;

export const FULLSCREEN_VS = `#version 300 es
precision highp float;
out vec2 vUv;
void main() {
  vec2 p = vec2((gl_VertexID == 1) ? 3.0 : -1.0, (gl_VertexID == 2) ? 3.0 : -1.0);
  vUv = p * 0.5 + 0.5;
  gl_Position = vec4(p, 1.0, 1.0);
}
`;

export const SKY_FS = `#version 300 es
precision highp float;
in vec2 vUv;
uniform mat4 uInvVP;
uniform vec3 uCamPos;
uniform vec3 uSkyTop;
uniform vec3 uSkyHorizon;
uniform vec3 uSkyBottom;
uniform vec3 uSunDir;
uniform vec3 uSunColor;
uniform float uSunDisc;
layout(location=0) out vec4 oColor;
layout(location=1) out vec4 oNormal;
void main() {
  vec2 ndc = vUv * 2.0 - 1.0;
  vec4 f = uInvVP * vec4(ndc, 1.0, 1.0);
  vec3 dir = normalize(f.xyz / f.w - uCamPos);
  float t = dir.y;
  vec3 col = t > 0.0 ? mix(uSkyHorizon, uSkyTop, pow(t, 0.55)) : mix(uSkyHorizon, uSkyBottom, pow(-t, 0.5));
  float s = dot(dir, uSunDir);
  col += uSunColor * (smoothstep(0.9975, 0.999, s) * uSunDisc + pow(max(s, 0.0), 48.0) * 0.18);
  oColor = vec4(col, 1.0);
  oNormal = vec4(0.0);
}
`;

export const POST_FS = `#version 300 es
precision highp float;
in vec2 vUv;
uniform sampler2D uColor;
uniform sampler2D uNormal;
uniform sampler2D uDepth;
uniform vec2 uResolution;
uniform vec2 uNearFar;
uniform float uTime;
uniform float uVignette;
uniform float uFade;
uniform float uWhite;
uniform float uHeat;
uniform float uOutline;
uniform float uDesat;
uniform vec3 uTint;
uniform vec2 uFogRange;
out vec4 oColor;

float linDepth(float z) {
  float n = uNearFar.x, f = uNearFar.y;
  float zn = z * 2.0 - 1.0;
  return 2.0 * n * f / (f + n - zn * (f - n));
}

void main() {
  vec2 px = 1.0 / uResolution;
  vec2 uv = vUv;
  // heat shimmer: distant pixels wobble horizontally
  if (uHeat > 0.0) {
    float d0 = linDepth(texture(uDepth, uv).r);
    float far = smoothstep(25.0, 90.0, d0);
    uv.x += sin(uv.y * 90.0 + uTime * 2.3) * sin(uv.y * 31.0 - uTime * 1.1) * 0.0035 * uHeat * far;
    uv.y += cos(uv.x * 70.0 + uTime * 1.9) * 0.0015 * uHeat * far;
  }
  vec3 col = texture(uColor, uv).rgb;

  // depth + normal edge detection (2px outline)
  if (uOutline > 0.0) {
    float dc = linDepth(texture(uDepth, uv).r);
    vec4 nc4 = texture(uNormal, uv);
    vec3 nc = nc4.rgb * 2.0 - 1.0;
    float edge = 0.0;
    vec2 offs[4] = vec2[4](vec2(px.x, 0.0), vec2(-px.x, 0.0), vec2(0.0, px.y), vec2(0.0, -px.y));
    for (int i = 0; i < 4; i++) {
      vec2 o = offs[i] * 1.5;
      float d = linDepth(texture(uDepth, uv + o).r);
      vec4 n4 = texture(uNormal, uv + o);
      vec3 n = n4.rgb * 2.0 - 1.0;
      float dd = abs(d - dc) / max(dc, 0.5);
      edge += smoothstep(0.03, 0.08, dd);
      if (nc4.a > 0.5 && n4.a > 0.5) edge += smoothstep(0.55, 0.75, 1.0 - dot(n, nc)) * 0.6;
    }
    edge = clamp(edge, 0.0, 1.0);
    float fade = 1.0 - smoothstep(uFogRange.x, uFogRange.y, dc);
    col = mix(col, col * 0.35, edge * uOutline * fade);
  }

  // desaturate (chalice grab) and tint
  float lum = dot(col, vec3(0.299, 0.587, 0.114));
  col = mix(col, vec3(lum), uDesat);
  col *= uTint;

  // vignette
  vec2 q = vUv - 0.5;
  float v = 1.0 - dot(q, q) * uVignette;
  col *= clamp(v, 0.0, 1.0);

  col = mix(col, vec3(1.0), uWhite);
  col = mix(col, vec3(0.0), uFade);
  oColor = vec4(col, 1.0);
}
`;
